<template>
  <div>
    <!-- Filter Drawer Overlay -->
    <div v-if="show" class="filter-overlay" @click="closeDrawer"></div>
    
    <!-- Filter Drawer -->
    <div class="filter-drawer" :class="{ 'filter-drawer-open': show }">
      <div class="filter-content">
        <div class="filter-header">
          <h5 class="mb-0">Bộ lọc tìm kiếm</h5>
          <div class="header-actions">
            <b-button size="sm" variant="outline-secondary" @click="clearAllFilters" class="mr-2">
              <b-icon icon="x-circle"></b-icon> Xóa tất cả
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="closeDrawer">
              <b-icon icon="x"></b-icon>
            </b-button>
          </div>
        </div>

        <div class="filter-body">
          <div v-for="(filter, filterIndex) in filters" :key="filterIndex" class="filter-group mb-4">
            <div class="filter-label-row">
              <label class="filter-label">{{getColumnAlias(filter.col)}}</label>
              <b-button size="sm" variant="link" class="p-0 filter-clear-btn" @click="clearFilter(filter)">
                <b-icon icon="x-circle" class="mr-1"></b-icon> Xóa
              </b-button>
            </div>
            
            <!-- Text Filter -->
            <template v-if="filter.type === 'text'">
              <b-form-input 
                type="text" 
                v-model="filter.value" 
                :placeholder="getColumnAlias(filter.col)"
                size="sm"/>
            </template>

            <!-- Select Filter -->
            <template v-if="filter.type === 'select'">
              <b-form-select 
                v-model="filter.value" 
                :options="getColumnOptions(filter.col)"
                size="sm">
                <template #first>
                  <b-form-select-option :value="null">-- Chọn --</b-form-select-option>
                </template>
              </b-form-select>
            </template>

            <!-- Multi-select Filter -->
            <template v-if="filter.type === 'multi-select'">
              <div class="multi-select-container">
                <b-form-checkbox-group
                  v-model="filter.value"
                  :options="getColumnOptions(filter.col)"
                  stacked
                  size="sm"
                  class="multi-select-group">
                </b-form-checkbox-group>
              </div>
            </template>

            <!-- Number Range Filter -->
            <template v-if="filter.type === 'number'">
              <div class="number-range">
                <b-form-input 
                  type="number" 
                  v-model.number="filter.from" 
                  placeholder="Từ" 
                  size="sm"
                  class="mb-2"
                  inputmode="decimal"/>
                <b-form-input 
                  type="number" 
                  v-model.number="filter.to" 
                  placeholder="Đến" 
                  size="sm"
                  inputmode="decimal"/>
              </div>
            </template>

            <!-- Boolean Filter -->
            <template v-if="filter.type === 'boolean'">
              <b-form-checkbox
                v-model="filter.value"
                :value="true"
                :unchecked-value="false"
                size="sm">
                {{getColumnAlias(filter.col)}}
              </b-form-checkbox>
            </template>
          </div>
        </div>

        <div class="filter-footer">
          <b-button variant="primary" @click="applyFilters" class="w-100 mb-2">
            <b-icon icon="search"></b-icon> Áp dụng bộ lọc
          </b-button>
          <b-button variant="outline-secondary" @click="closeDrawer" class="w-100">
            Đóng
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FilterDrawer',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Array,
      required: true
    },
    columns: {
      type: Object,
      required: true
    },
    fallbackOptions: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Prevent body scroll when drawer is open
        document.body.style.overflow = 'hidden';
        // Initialize filters when drawer opens
        this.initializeFilters();
      } else {
        // Restore body scroll
        document.body.style.overflow = '';
      }
    }
  },
  mounted() {
    // Initialize filters when component is first mounted
    this.initializeFilters();
  },
  beforeDestroy() {
    // Restore body scroll if component is destroyed while drawer is open
    document.body.style.overflow = '';
  },
  methods: {
    initializeFilters() {
      // Ensure multi-select filters have proper array values
      this.filters.forEach(filter => {
        if (filter.type === 'multi-select') {
          if (!Array.isArray(filter.value)) {
            // Convert string to array or initialize as empty array
            filter.value = filter.value ? [filter.value] : [];
            console.log('FilterDrawer: Converted multi-select filter to array:', filter.col, filter.value);
          }
        }
      });
    },

    getColumnAlias(columnName) {
      const column = this.columns[columnName];
      return column ? column.alias : columnName;
    },

    getColumnOptions(columnName) {
      const column = this.columns[columnName];
      console.log('Getting options for column:', columnName, column);
      
      if (column && column.options) {
        if (Array.isArray(column.options)) {
          // Simple array of options - return as is for checkbox group
          console.log('Array options for', columnName, ':', column.options);
          return column.options.map(option => ({
            text: option,
            value: option
          }));
        } else if (typeof column.options === 'object') {
          // Object with key-value pairs
          const options = Object.keys(column.options).map(key => ({
            text: column.options[key],
            value: key
          }));
          console.log('Object options for', columnName, ':', options);
          return options;
        }
      }
      
      // Check for fallback options provided by parent
      if (this.fallbackOptions[columnName]) {
        return this.fallbackOptions[columnName];
      }
      
      console.log('No options found for column:', columnName);
      return [];
    },

    clearAllFilters() {
      this.filters.forEach(filter => {
        if (filter.type === 'multi-select') {
          filter.value = [];
        } else {
          filter.value = null;
        }
        filter.from = null;
        filter.to = null;
      });
      this.$emit('clear-filters');
    },

    clearFilter(filter) {
      if (!filter) return;
      if (filter.type === 'multi-select') {
        filter.value = [];
      } else if (filter.type === 'number') {
        filter.from = null;
        filter.to = null;
      } else if (filter.type === 'boolean') {
        // set to null so it won't participate in query building
        filter.value = null;
      } else {
        filter.value = null;
      }
    },

    applyFilters() {
      this.$emit('apply-filters');
    },

    closeDrawer() {
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
/* Filter Drawer Overlay */
.filter-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  opacity: 0;
  animation: fadeIn 0.3s ease-out forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

/* Filter Drawer Styles */
.filter-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 350px;
  background: white;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  transform: translateX(100%);
  transition: transform 0.3s ease-out;
}

.filter-drawer-open {
  transform: translateX(0);
}

.filter-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.filter-header {
  padding: 20px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
}

.filter-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.filter-group {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
}

.filter-group:last-child {
  border-bottom: none;
}

.filter-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  display: block;
  font-size: 0.9rem;
}

.filter-clear-btn {
  color: #6c757d;
}

.multi-select-container {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  padding: 8px;
  background: white;
}

.multi-select-group {
  margin: 0;
}

.multi-select-group .custom-control {
  margin-bottom: 4px;
}

.multi-select-group .custom-control:last-child {
  margin-bottom: 0;
}

.number-range {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-footer {
  padding: 20px;
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
}

/* Mobile responsive adjustments for drawer */
@media (max-width: 576px) {
  .filter-drawer {
    width: 100% !important;
    left: 0;
  }
  
  .filter-header,
  .filter-body,
  .filter-footer {
    padding: 15px;
  }
  
  .multi-select-container {
    max-height: 150px;
  }
  
  .header-actions {
    flex-wrap: wrap;
    gap: 5px;
  }
}

@media (max-width: 768px) {
  .filter-drawer {
    width: 100%;
    left: 0;
  }
}
</style> 